/**
 * EntryFormsPanel — Entry Forms (Areas & above) Documents tile.
 *
 * Two single-page A4 PDF generators:
 *   1. Individual & Pairs entry form (skater bio + per-discipline grade
 *      dropdown + Achievement-Test number cell + partner cell)
 *   2. Teams / Groups entry form (team header + 15 × 2 = 30 member slots)
 *
 * Layout matches the Final Programme tile (button on left, label +
 * tagline on the right) per Feb 2026 design brief. Both downloads are
 * standalone PDFs — designed for the club secretary to email to skaters
 * rather than print, hence the "for emailing" tagline wording.
 *
 * The forms only meaningfully fire on Area/State and above competitions —
 * smaller comps don't strictly need them — but the API doesn't enforce
 * that gate (the user may want to download for inspection regardless).
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download } from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


export default function EntryFormsPanel({ competitionId }) {
  const [downloadingIndividual, setDownloadingIndividual] = useState(false);
  const [downloadingGroup, setDownloadingGroup] = useState(false);

  const download = async (kind) => {
    if (!competitionId) return;
    const isIndividual = kind === 'individual';
    if (isIndividual) setDownloadingIndividual(true);
    else setDownloadingGroup(true);
    try {
      const url = `${API}/documents/entry-forms/${kind}?competition_id=${competitionId}`;
      const response = await axios.post(url, null, {
        withCredentials: true, responseType: 'blob',
      });
      const disposition = response.headers['content-disposition'] || '';
      let filename = isIndividual
        ? 'Individual_Entry_Form.pdf'
        : 'Group_Entry_Form.pdf';
      const match = disposition.match(/filename="?([^";\n]+)"?/);
      if (match) filename = match[1];
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`${isIndividual ? 'Individual & Pairs' : 'Teams/Groups'} entry form downloaded`);
    } catch (error) {
      const detail = error.response?.data?.detail
        || (error.message || 'Failed to generate entry form');
      toast.error(detail);
    } finally {
      if (isIndividual) setDownloadingIndividual(false);
      else setDownloadingGroup(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-stone-800 mb-2">Entry Forms (Areas & above)</h3>
        <p className="text-sm text-muted-foreground">
          Generate the entry forms used by clubs to collect skater and team
          entries. Two separate PDFs are produced — one for individual and pairs
          entries, one for team/group entries. Designed for emailing to clubs.
        </p>
      </div>

      {!competitionId && (
        <div
          data-testid="entry-forms-no-comp"
          className="rounded-lg border bg-stone-50 px-3 py-3 text-sm text-stone-600"
        >
          Pick a competition above to continue.
        </div>
      )}

      {competitionId && (
        <Card
          className="p-5 space-y-4 border-stone-200 bg-stone-50/50"
          data-testid="entry-forms-tile"
        >
          <div>
            <h4 className="text-base font-semibold text-stone-800">
              Create Entry Forms (Areas and Above) for Download
            </h4>
          </div>

          {/* Individual & Pairs */}
          <div className="grid grid-cols-[260px_1fr] gap-4 items-center">
            <Button
              onClick={() => download('individual')}
              disabled={downloadingIndividual}
              className="bg-[#5C4033] hover:bg-[#3d2a22] text-white w-[260px]"
              data-testid="entry-form-individual-btn"
            >
              <Download
                className={`mr-2 h-4 w-4 ${downloadingIndividual ? 'animate-pulse' : ''}`}
              />
              {downloadingIndividual ? 'Generating...' : 'Entries — Individual & Pairs'}
            </Button>
            <div>
              <p className="text-sm font-medium text-stone-800">
                Generate the individual & pairs entry form for emailing
              </p>
              <p className="text-xs text-stone-600">
                One A4 PDF covering Figures, Loops, Solo Dance, Solo
                Freedance/Styledance, Freeskating, Inline Freeskating,
                Freeskating Pairs and Couples Dance. Includes a grade-option
                hint per discipline drawn from this competition&rsquo;s grades.
              </p>
            </div>
          </div>

          {/* Teams / Groups */}
          <div className="grid grid-cols-[260px_1fr] gap-4 items-center">
            <Button
              onClick={() => download('group')}
              disabled={downloadingGroup}
              className="bg-[#5C4033] hover:bg-[#3d2a22] text-white w-[260px]"
              data-testid="entry-form-group-btn"
            >
              <Download
                className={`mr-2 h-4 w-4 ${downloadingGroup ? 'animate-pulse' : ''}`}
              />
              {downloadingGroup ? 'Generating...' : 'Entries — Teams / Groups'}
            </Button>
            <div>
              <p className="text-sm font-medium text-stone-800">
                Generate the teams/groups entry form for emailing
              </p>
              <p className="text-xs text-stone-600">
                One A4 PDF for Precision, Quartets, Show Skate and Whakaatuanga
                entries. Captures the team name, club, discipline + grade and
                up to 30 skater slots (15 rows × 2 columns) including the
                &ldquo;Only Event&rdquo; flag for skaters in the team only.
              </p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
